import { computed, type ComputedRef, ref } from 'vue';

export const SnackbarStatus = {
  SUCCESS: 'success',
  ERROR: 'error',
  DEFAULT: 'default'
} as const;

const Icon = {
  [SnackbarStatus.SUCCESS]: '$success',
  [SnackbarStatus.ERROR]: '$clear'
} as const;

const SnackbarLocation = {
  TOP_RIGHT: 'top right',
  BOTTOM_RIGHT: 'bottom right'
} as const;

const ColorOptions = {
  SURFACE: 'surface',
  SUCCESS: 'success',
  ERROR: 'error'
} as const;

type StatusTypes = (typeof SnackbarStatus)[keyof typeof SnackbarStatus];
type LocationTypes = (typeof SnackbarLocation)[keyof typeof SnackbarLocation];
type SnackbarBackgroundType = typeof ColorOptions.SURFACE | undefined;
type IconColorTypes = typeof ColorOptions.SUCCESS | typeof ColorOptions.ERROR;

type IconTypes = {
  color: IconColorTypes;
  icon: (typeof Icon)[keyof typeof Icon];
};

interface ShowSnackbarOptions {
  message: string;
  status?: StatusTypes;
  color?: SnackbarBackgroundType;
  timeout?: number;
}

const DEFAULT_TIMEOUT = 3000; // default 3 sec
const STATUS_CLASSES = 'text-body-1';

const isSnackbarVisible = ref<boolean>(false);
const message = ref<string>('');
const status = ref<StatusTypes>(SnackbarStatus.DEFAULT);
const customTimeout = ref<number | undefined>(undefined);

interface SnackbarMessage {
  isSnackbarVisible: typeof isSnackbarVisible;
  message: typeof message;
  status: typeof status;
  color: ComputedRef<SnackbarBackgroundType | undefined>;
  hasSnackbarCloseBtn: ComputedRef<boolean>;
  location: ComputedRef<LocationTypes>;
  icon: ComputedRef<IconTypes | undefined>;
  customClasses: ComputedRef<string | undefined>;
  timeout: ComputedRef<number>;
  showSnackbar: (options: ShowSnackbarOptions) => void;
  closeSnackbar: () => void;
}

/**
 * A snackbar has two variations, one is the "status" version, which will be located in the top-right and will auto hide after the timeout.
 * This version will be white (surface), and has simple icons to go along with the "status".
 *
 * The other is the "default" version, which will be located in the bottom-right and requires user interaction to close, and include an action button.
 * This version will have the gray background (surface-variant) and will remain open, unless otherwise indicated when the showSnackbar function is set.
 * In the future, we will add the ability to include a callback function
 */
export const useSnackbar = (): SnackbarMessage => {
  const showSnackbar = ({
    status: customStatus = SnackbarStatus.DEFAULT, // used to determine if we are using the status or default version
    message: msg,
    timeout
  }: ShowSnackbarOptions) => {
    message.value = msg;
    status.value = customStatus;
    customTimeout.value = timeout;
    isSnackbarVisible.value = true;
  };

  const closeSnackbar = () => {
    isSnackbarVisible.value = false;
  };

  const hasSnackbarCloseBtn = computed(
    () => status.value === SnackbarStatus.DEFAULT
  );

  const icon = computed<IconTypes | undefined>(() => {
    switch (status.value) {
      case SnackbarStatus.SUCCESS:
        return {
          color: ColorOptions.SUCCESS,
          icon: Icon[SnackbarStatus.SUCCESS]
        };
      case SnackbarStatus.ERROR:
        return {
          color: ColorOptions.ERROR,
          icon: Icon[SnackbarStatus.ERROR]
        };
      default:
        return undefined;
    }
  });

  const color = computed<SnackbarBackgroundType | undefined>(() => {
    if (status.value !== SnackbarStatus.DEFAULT) {
      return ColorOptions.SURFACE;
    }

    return undefined;
  });

  const location = computed<LocationTypes>(() => {
    // The status versions will be located in the top-right, while the default version will use the default location
    if (status.value !== SnackbarStatus.DEFAULT) {
      return SnackbarLocation.TOP_RIGHT;
    }

    return SnackbarLocation.BOTTOM_RIGHT;
  });

  const customClasses = computed<string | undefined>(() => {
    // Status versions will increase the font-size
    if (status.value !== SnackbarStatus.DEFAULT) {
      return STATUS_CLASSES;
    }

    return undefined;
  });

  const timeout = computed<number>(() => {
    // Default version should stay open unless otherwise indicated
    if (status.value === SnackbarStatus.DEFAULT) {
      return customTimeout.value || -1;
    }

    return customTimeout.value || DEFAULT_TIMEOUT;
  });

  return {
    isSnackbarVisible,
    hasSnackbarCloseBtn,
    message,
    status,
    color,
    location,
    timeout,
    icon,
    customClasses,
    showSnackbar,
    closeSnackbar
  };
};
